import React from "react";
import { formatMatchStatusForDisplay } from "../../Games/MatchCards/matchCardCompactUi";
import {
    normalizePrematchCountdownSpacing,
    parsePrematchCountdownParts,
} from "../../../utils/matchCountdown";

/** Gentle opacity pulse on the duration portion of pre-kickoff countdown labels. */
export const PREMATCH_COUNTDOWN_FLASH_CLASS = "prematch-countdown-flash";

/**
 * True only for Tailwind display utilities (`flex`, `inline-flex`, including variants).
 * `flex-1` / `flex-none` set grow, not display — treating them as flex drops the gap
 * between "STARTS IN" and the time (`STARTS IN1H`).
 */
function classNameSetsFlexDisplay(className: string): boolean {
    return className.split(/\s+/).some((token) => {
        const base = token.split(":").pop() ?? "";
        return base === "flex" || base === "inline-flex";
    });
}

type PrematchCountdownLabelProps = {
    text: string;
    className?: string;
    durationClassName?: string;
    style?: React.CSSProperties;
};

/** Renders `STARTS IN 1H 56M`-style labels with a soft flash on the time portion only. */
export function PrematchCountdownLabel({
    text,
    className = "",
    durationClassName = PREMATCH_COUNTDOWN_FLASH_CLASS,
    style,
}: PrematchCountdownLabelProps) {
    const normalizedText = normalizePrematchCountdownSpacing(text);
    const parts = parsePrematchCountdownParts(normalizedText);
    if (!parts) {
        return (
            <span className={className} style={style}>
                {formatMatchStatusForDisplay(normalizedText)}
            </span>
        );
    }

    const hasFlexLayout = classNameSetsFlexDisplay(className);
    const splitClassName = hasFlexLayout ? className : `${className} inline-flex items-center`.trim();
    const splitStyle: React.CSSProperties = {
        ...style,
        columnGap: "0.35em",
    };

    return (
        <span className={splitClassName} style={splitStyle}>
            <span className="shrink-0">{parts.prefix.trimEnd()}</span>
            <span className={durationClassName}>{parts.duration}</span>
        </span>
    );
}
